import { useEffect, useState } from "react";
import axios from "axios";
import { Bell, Check } from "lucide-react";
import { API } from "../lib/helpers";
import { toast } from "sonner";

/**
 * Small opt-in bell toggle for per-category deal alerts.
 *
 * Design rules (critical — do NOT make this loud):
 *   - Only rendered for authenticated users. Anonymous users see nothing.
 *   - Default OFF. Pure opt-in.
 *   - Single-click to enable; another click to disable.
 *   - No default-on nudges, no modals, no interstitials.
 *
 * Props:
 *   - category: "top-deals" | "best-deals-under-300k" | "top-multifamily-deals"
 *   - isAuthenticated: boolean
 */
export default function DealAlertToggle({ category, isAuthenticated }) {
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Record the view (opt-in signal only — never auto-subscribes).
    axios.post(`${API}/deal-alerts/view`, { category }).catch(() => {});
    // Fetch current opt-in state so the bell renders correctly.
    axios
      .get(`${API}/deal-alerts`)
      .then((r) => {
        setEnabled(!!r.data?.subscriptions?.[category]);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [category, isAuthenticated]);

  if (!isAuthenticated || !loaded) return null;

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    const next = !enabled;
    // Optimistic update
    setEnabled(next);
    try {
      const path = next ? "subscribe" : "unsubscribe";
      await axios.post(`${API}/deal-alerts/${path}`, { category });
      toast.success(
        next
          ? "You'll get a daily email when new deals are added — one per day max."
          : "Alert off. You won't receive deal emails for this category.",
        { duration: 4000 }
      );
    } catch (e) {
      // Revert on failure
      setEnabled(!next);
      toast.error("Couldn't update alert preference. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`deal-alert-toggle-${category}`}
      aria-pressed={enabled}
      className={
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors " +
        (enabled
          ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/25"
          : "bg-secondary/40 text-muted-foreground border-border hover:bg-secondary")
      }
    >
      {enabled ? (
        <>
          <Check className="w-3.5 h-3.5" />
          <span>Alerts on</span>
        </>
      ) : (
        <>
          <Bell className="w-3.5 h-3.5" />
          <span>Get an email when new deals are added</span>
        </>
      )}
    </button>
  );
}
